/* ==========================================================================
   preset-packs.js  —  Streaky Audio preset packs page
   --------------------------------------------------------------------------
   Vanilla, dependency-free. Self-contained per section instance.

   Responsibilities:
     - Open/close the detail frame (modal) with focus trap, Esc, click-outside,
       body scroll lock and focus return.
     - Deep linking: ?pack=<handle> opens the matching frame on load and is kept
       in sync via history.pushState. Only the `pack` param is touched — utm_*,
       fbclid and everything else are preserved. Back button closes the frame.
     - AJAX add-to-cart (/cart/add.js) for cards; add-then-/checkout for Buy Now.
     - ViewContent on frame open (mode configurable: off | fbq | event).

   Theme-specific wiring lives behind config + the onCartAdded() hook, both
   clearly marked "FINALIZE ON RECONNECT" — see assets notes in HANDOVER.md.
   ========================================================================== */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-preset-packs]');
  if (!roots.length) return;
  roots.forEach(initSection);

  function initSection(root) {
    var config = readConfig(root);
    var routeRoot = (config.routesRoot || '/').replace(/\/+$/, '/') || '/';
    if (routeRoot.slice(-1) !== '/') routeRoot += '/';

    // ---- state ----------------------------------------------------------
    var openEl = null;          // currently-open .pp-modal element (or null)
    var lastTrigger = null;     // element to return focus to on close
    var firedFor = null;        // handle we last fired ViewContent for (dedupe)

    // ---- wire triggers --------------------------------------------------
    root.querySelectorAll('[data-pp-details]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        userOpen(el.getAttribute('data-pp-details'), el);
      });
    });

    // Optional progressive-enhancement: if any [data-pp-addform] <form> is used,
    // intercept its submit for AJAX. (Cards use [data-pp-add] buttons by default.)
    root.querySelectorAll('[data-pp-addform]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var btn = form.querySelector('[type="submit"], button');
        addToCart(form.querySelector('[name="id"]').value, btn, { checkout: false });
      });
    });

    // Buy Now (modal): add then go straight to checkout.
    root.querySelectorAll('[data-pp-buy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        addToCart(btn.getAttribute('data-variant-id'), btn, { checkout: true });
      });
    });

    // Optional secondary add-to-cart inside the modal.
    root.querySelectorAll('[data-pp-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        addToCart(btn.getAttribute('data-variant-id'), btn, { checkout: false });
      });
    });

    // ---- wire each modal ------------------------------------------------
    root.querySelectorAll('.pp-modal').forEach(function (modal) {
      modal.querySelectorAll('[data-pp-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { userClose(); });
      });
      var overlay = modal.querySelector('.pp-modal__overlay');
      if (overlay) overlay.addEventListener('click', function () { userClose(); });
      modal.querySelectorAll('audio, video[controls]').forEach(wireAudio);
    });

    // ---- reviews: expand panel + show-more --------------------------------
    root.querySelectorAll('[data-pp-reviews-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-pp-reviews]');
        if (!wrap) return;
        var panel = wrap.querySelector('[data-pp-reviews-panel]');
        var open = !wrap.classList.contains('is-open');
        wrap.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });
    });
    root.querySelectorAll('[data-pp-reviews-more]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-pp-reviews]');
        if (wrap) wrap.classList.add('is-expanded');
        btn.hidden = true;
      });
    });

    // ---- global key + history handlers ----------------------------------
    document.addEventListener('keydown', function (e) {
      if (!openEl) return;
      if (e.key === 'Escape') { e.preventDefault(); userClose(); }
      else if (e.key === 'Tab') trapTab(e);
    });
    window.addEventListener('popstate', function () { applyState(packFromUrl()); });

    // ---- initial deep link ----------------------------------------------
    var initialPack = packFromUrl();
    if (initialPack && findModal(initialPack)) {
      // Return focus to the matching card's Details button when the frame closes.
      lastTrigger = root.querySelector('[data-pp-details="' + cssEscape(initialPack) + '"]');
      // Seed a clean entry *behind* the open frame so Back closes it instead of
      // leaving the page, while the address bar still shows ?pack=<handle>.
      history.replaceState({}, '', buildUrl(null));
      applyState(initialPack);
      history.pushState({ pack: initialPack }, '', buildUrl(initialPack));
    }

    /* ===================================================================
       Modal open / close
       =================================================================== */

    // User-initiated open: change state AND push history.
    function userOpen(handle, trigger) {
      if (!findModal(handle)) return;
      lastTrigger = trigger || document.activeElement;
      applyState(handle);
      history.pushState({ pack: handle }, '', buildUrl(handle));
    }

    // User-initiated close (X / Esc / overlay): change state and drop ?pack.
    // replaceState (not push) so a later Back doesn't re-open the just-closed frame.
    function userClose() {
      if (!openEl) return;
      applyState(null);
      history.replaceState({}, '', buildUrl(null));
    }

    // Reconcile the DOM to a target handle WITHOUT touching history.
    // Called by userOpen/userClose (after they push) and by popstate / initial load.
    function applyState(handle) {
      var target = handle ? findModal(handle) : null;

      // close whatever's open if it's not the target
      if (openEl && openEl !== target) hide(openEl);

      if (target) {
        if (openEl !== target) show(target);
        openEl = target;
        lockScroll(true);
        focusFirst(target);
        fireViewContent(target);
      } else {
        openEl = null;
        lockScroll(false);
        firedFor = null;
        if (lastTrigger && typeof lastTrigger.focus === 'function') {
          lastTrigger.focus();
          lastTrigger = null;
        }
      }
    }

    function show(modal) { modal.hidden = false; }
    function hide(modal) {
      modal.querySelectorAll('audio, video').forEach(function (m) { m.pause(); });
      modal.hidden = true;
    }

    function findModal(handle) {
      if (!handle) return null;
      return root.querySelector('.pp-modal[data-handle="' + cssEscape(handle) + '"]');
    }

    function lockScroll(on) {
      document.documentElement.classList.toggle('pp-modal-open', !!on);
    }

    /* ===================================================================
       Focus management
       =================================================================== */

    var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),' +
      'select:not([disabled]),textarea:not([disabled]),audio[controls],[tabindex]:not([tabindex="-1"])';

    function focusFirst(modal) {
      var frame = modal.querySelector('.pp-modal__frame') || modal;
      var first = frame.querySelector('[data-pp-close]') || frame.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    function trapTab(e) {
      var frame = openEl.querySelector('.pp-modal__frame') || openEl;
      var items = Array.prototype.filter.call(frame.querySelectorAll(FOCUSABLE), isVisible);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function isVisible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }

    /* ===================================================================
       URL helpers — only the `pack` param is ever touched.
       =================================================================== */

    function packFromUrl() {
      try { return new URL(window.location.href).searchParams.get('pack'); }
      catch (e) { return null; }
    }
    function buildUrl(handle) {
      var url = new URL(window.location.href);
      if (handle) url.searchParams.set('pack', handle);
      else url.searchParams.delete('pack');
      return url.pathname + url.search + url.hash;
    }

    /* ===================================================================
       Audio — load only on first play; never autoplay; one at a time.
       =================================================================== */

    // preload="none" (set in Liquid) keeps the file from loading until play is
    // pressed. Here we just ensure only one demo plays at a time (the muted
    // autoplay hero video is left alone).
    function wireAudio(media) {
      media.addEventListener('play', function () {
        root.querySelectorAll('audio, video[controls]').forEach(function (other) {
          if (other !== media) other.pause();
        });
      });
    }

    /* ===================================================================
       ViewContent — fire once per frame open.
       mode: 'off' (default until confirmed with Matt) | 'fbq' | 'event'
       =================================================================== */

    function fireViewContent(modal) {
      var handle = modal.getAttribute('data-handle');
      if (firedFor === handle) return;      // dedupe within a single open
      firedFor = handle;
      if (config.pixelMode === 'off' || !config.pixelMode) return;

      var idAttr = config.contentIdType === 'variant' ? 'data-variant-id' : 'data-product-id';
      var idEl = modal.querySelector('[' + idAttr + ']');
      var payload = {
        content_ids: [idEl ? idEl.getAttribute(idAttr) : handle],
        content_name: modal.getAttribute('data-title') || handle,
        content_type: 'product',
        value: parseFloat(modal.getAttribute('data-price') || '0') || 0,
        currency: modal.getAttribute('data-currency') || config.currency || 'GBP'
      };

      if (config.pixelMode === 'fbq' && typeof window.fbq === 'function') {
        window.fbq('track', 'ViewContent', payload);
      } else {
        // Custom Pixel / Meta sales-channel path: a subscriber forwards this.
        document.dispatchEvent(new CustomEvent('preset-packs:view-content', { detail: payload }));
      }
    }

    /* ===================================================================
       Cart — AJAX add; native checkout/cart untouched.
       =================================================================== */

    function addToCart(variantId, btn, opts) {
      if (!variantId) return;
      setLoading(btn, true);
      var body = { items: [{ id: Number(variantId), quantity: 1 }] };
      // Ask Shopify to re-render the cart drawer sections in the same round-trip.
      if (config.cartMode === 'drawer' && config.cartSections && config.cartSections.length) {
        body.sections = config.cartSections;
        body.sections_url = window.location.pathname;
      }
      fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw res.data;
          if (opts.checkout) { window.location.href = routeRoot + 'pages/complete-your-setup'; return; }
          onCartAdded(res.data, btn);
        })
        .catch(function (err) {
          console.error('[preset-packs] add to cart failed', err);
          // Safe fallback: send them to the cart page so nothing is lost.
          window.location.href = routeRoot + 'cart';
        })
        .finally(function () { setLoading(btn, false); });
    }

    /* -------------------------------------------------------------------
       FINALIZE ON RECONNECT: match this to the live theme's cart drawer.
       Default behaviour (works on Dawn-derived themes):
         1. re-render returned sections into matching [id] nodes,
         2. open a <cart-drawer> element if present,
         3. dispatch 'preset-packs:added' for any theme listeners,
         4. fall back to the cart page if there is no drawer.
       Set config.cartMode = 'redirect' to skip the drawer entirely.
       ------------------------------------------------------------------- */
    function onCartAdded(data, btn) {
      document.dispatchEvent(new CustomEvent('preset-packs:added', { detail: data }));

      if (config.cartMode === 'redirect') { window.location.href = routeRoot + 'pages/complete-your-setup'; return; }

      if (data && data.sections) {
        Object.keys(data.sections).forEach(function (id) {
          var host = document.getElementById(id) ||
            document.querySelector('[id^="shopify-section-' + id + '"]');
          if (host && typeof data.sections[id] === 'string') host.innerHTML = data.sections[id];
        });
      }

      var drawer = document.querySelector(config.cartDrawerSelector || 'cart-drawer');
      if (drawer) {
        if (typeof drawer.open === 'function') drawer.open();
        else drawer.classList.add('active', 'is-open');
        document.body.classList.add('overflow-hidden');
        return;
      }

      // No drawer found (this store hides the cart drawer via cart_type: page).
      // The sitewide cart bar (#st-cartbar) already surfaced the add through the
      // dispatched 'preset-packs:added' event, so stay on the page — never bounce
      // the shopper to /cart.
    }

    function setLoading(btn, on) {
      if (!btn) return;
      btn.classList.toggle('is-loading', !!on);
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
      btn.disabled = !!on;
    }
  }

  /* ===================================================================
     Shared helpers
     =================================================================== */

  function readConfig(root) {
    var defaults = {
      pixelMode: 'off',
      cartMode: 'drawer',
      cartDrawerSelector: 'cart-drawer',
      cartSections: [],
      contentIdType: 'product',
      currency: 'GBP',
      routesRoot: '/'
    };
    var node = root.querySelector('[data-pp-config]');
    if (!node) return defaults;
    try {
      var parsed = JSON.parse(node.textContent);
      var cfg = Object.assign(defaults, parsed);
      if (Array.isArray(cfg.cartSections)) {
        cfg.cartSections = cfg.cartSections.map(function (s) { return String(s).trim(); }).filter(Boolean);
      }
      return cfg;
    } catch (e) {
      console.warn('[preset-packs] bad config JSON, using defaults', e);
      return defaults;
    }
  }

  // CSS.escape with a tiny fallback for older browsers.
  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, function (ch) {
      return '\\' + ch;
    });
  }
})();
